import React, { useState, useEffect } from 'react';
import { Calendar, X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import BookingModal from './BookingModal';
import { useBooking } from '../context/BookingContext';
import { trackBookingClick } from '../hooks/useAnalytics';

const FloatingBookButton = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const { t } = useLanguage();
  const { bookingDates } = useBooking();

  useEffect(() => {
    const handleScroll = () => {
      // Show button after scrolling past the hero section
      // Lower threshold on mobile for better UX
      const scrollY = window.scrollY;
      const isMobile = window.innerWidth < 768;
      setIsVisible(scrollY > (isMobile ? 300 : 600));
    };

    window.addEventListener('scroll', handleScroll);
    // Check initial state
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleOpenBooking = () => {
    trackBookingClick();
    setIsBookingModalOpen(true);
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Floating Book Now Button - Optimized for mobile */}
      <div 
        className={`fixed z-40 transition-all duration-300 ${
          isMinimized 
            ? 'bottom-4 right-4 sm:bottom-6 sm:right-6' 
            : 'bottom-4 right-4 left-4 sm:left-auto sm:bottom-6 sm:right-6'
        } ${isMinimized ? 'scale-90' : 'scale-100'}`}
      >
        {isMinimized ? (
          // Minimized state - larger icon button for touch
          <button
            onClick={() => setIsMinimized(false)}
            className="w-16 h-16 sm:w-14 sm:h-14 bg-amber-700 hover:bg-amber-800 text-white rounded-full shadow-2xl flex items-center justify-center transition-all hover:scale-110 active:scale-95"
            data-testid="floating-book-btn-minimized"
            aria-label="Book Now"
          >
            <Calendar className="w-7 h-7 sm:w-6 sm:h-6" />
          </button>
        ) : (
          // Expanded state - full width on mobile, fixed width on desktop
          <div className="relative">
            {/* Close/minimize button */}
            <button
              onClick={() => setIsMinimized(true)}
              className="absolute -top-2 -right-2 sm:-top-2 sm:-right-2 w-7 h-7 sm:w-6 sm:h-6 bg-slate-700 hover:bg-slate-800 text-white rounded-full flex items-center justify-center shadow-lg transition-colors z-10"
              data-testid="minimize-floating-btn"
              aria-label="Minimize"
            >
              <X className="w-4 h-4 sm:w-3 sm:h-3" />
            </button>

            {/* Main booking button - full width on mobile */}
            <button
              onClick={handleOpenBooking}
              className="group w-full sm:w-auto bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white px-5 sm:px-6 py-4 sm:py-4 rounded-2xl shadow-2xl flex items-center justify-center sm:justify-start gap-3 sm:gap-3 transition-all hover:scale-[1.02] active:scale-[0.98] hover:shadow-amber-500/25"
              data-testid="floating-book-btn"
            >
              <div className="w-12 h-12 sm:w-12 sm:h-12 bg-white/20 rounded-xl flex items-center justify-center group-hover:bg-white/30 transition-colors flex-shrink-0">
                <Calendar className="w-6 h-6 sm:w-6 sm:h-6" />
              </div>
              <div className="text-left">
                <div className="font-bold text-lg sm:text-lg">{t('nav.bookNow') || 'Book Now'}</div>
                <div className="text-amber-100 text-sm">
                  {t('bestPrice.badge') || 'Best Price Guarantee'}
                </div>
              </div>
            </button>

            {/* Pulse animation ring - hidden on mobile to reduce distraction */}
            <div className="hidden sm:block absolute inset-0 rounded-2xl bg-amber-500 animate-ping opacity-20 pointer-events-none" />
          </div>
        )}
      </div>

      {/* Booking Modal */}
      <BookingModal 
        isOpen={isBookingModalOpen}
        onClose={() => setIsBookingModalOpen(false)}
        initialDates={{
          checkIn: bookingDates.checkIn,
          checkOut: bookingDates.checkOut,
          guests: bookingDates.guests
        }}
      />
    </>
  );
};

export default FloatingBookButton;
